import { useCallback, useEffect, useRef, useState, VoidFunctionComponent } from 'react';
import { PerspectiveCamera } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { Vector3, PerspectiveCamera as PerspectiveCameraImpl } from 'three';
import { getGeometrySize } from '@/lib/geometry';
import { Image } from '@/components/Image';
import { useResponsive } from '@/hooks/useResponsive';
import { BotIcon, ICON_ASPECT } from '@/components/BotIcon';
import connectControllerImg from '@/img/connect-controller.png';
import { deviceProperties } from '@/lib/deviceProperties';
import { OrientationPermissionState } from '@/lib/types';
import { BlinkingText } from '@/components/BlinkingText';
import { garamondNarrowLight } from '@/lib/fonts';
import { useMotionParallax } from '@/hooks/useMotionParallax';
import { useEnterOrSpace } from '@/hooks/useUserInput';

const CONNECT_ICON_ASPECT = 197 / 180;
const CONNECT_ICON_SCALAR = 0.4;
const initialTarget = new Vector3(0, 0, 0);
const initialPosition = new Vector3(0, 0, 5);

interface Props {
    goToNextScene: () => void;
    orientationPermission: OrientationPermissionState | undefined;
    requestOrientation: () => Promise<boolean>;
}

export const StartScreen: VoidFunctionComponent<Props> = ({
    goToNextScene,
    orientationPermission,
    requestOrientation,
}) => {
    const { supportsOrientation } = deviceProperties();
    const [fontSize, setFontSize] = useState<number>(0);
    const [connectIconSize, setConnectIconSize] = useState<[number, number]>([0, 0]);
    const { viewport } = useThree();
    const { responsiveValue } = useResponsive();
    const [animationComplete, setAnimationComplete] = useState<boolean>(false);
    const camera = useRef<PerspectiveCameraImpl>(null);
    useMotionParallax(initialTarget, initialPosition, [0, -0.5], [0, 1], [0, 3], [0, 3]);

    useEffect(() => {
        setFontSize(getGeometrySize(viewport.width, viewport.height, ICON_ASPECT, responsiveValue(0.02, 0.0475))[0]);
    }, [viewport, setFontSize, responsiveValue]);

    useEffect(() => {
        // Set the size of the onlybots icon based on the window size, trying
        // to fill whichever dimension is smallest
        setConnectIconSize(getGeometrySize(viewport.width, viewport.height, CONNECT_ICON_ASPECT));
    }, [viewport]);

    const askPermission = useCallback(() => {
        if (supportsOrientation && orientationPermission === 'notasked') {
            void requestOrientation();
        }
    }, [orientationPermission, requestOrientation, supportsOrientation]);

    const showConnectToController = supportsOrientation && orientationPermission === 'notasked';
    const onClick = showConnectToController ? askPermission : goToNextScene;
    useEnterOrSpace(goToNextScene);
    return (
        <>
            <PerspectiveCamera ref={camera} fov={75} far={1000} position={[0, 0, 5]} makeDefault>
                {animationComplete &&
                    (showConnectToController ? (
                        <Image
                            url={connectControllerImg}
                            scale={[
                                connectIconSize[0] * CONNECT_ICON_SCALAR,
                                connectIconSize[1] * CONNECT_ICON_SCALAR,
                                0,
                            ]}
                            position={[0, -0.2, -4]}
                            transparent
                        />
                    ) : (
                        <BlinkingText
                            font={garamondNarrowLight}
                            fontSize={fontSize}
                            color="white"
                            anchorX="center"
                            anchorY="middle"
                            position={[0, -0.02, -4]}
                        >
                            PRESS START
                        </BlinkingText>
                    ))}
            </PerspectiveCamera>
            <BotIcon
                initialOpacity={1}
                initialScale={0.1775}
                onAnimationComplete={() => setAnimationComplete(true)}
                onClick={onClick}
            />
        </>
    );
};
